/* eslint-disable @typescript-eslint/indent */
/* eslint-disable @next/next/no-img-element */
import { randomId } from '@pwskills/rachnaui/utils';
import React, { useEffect, useRef, useState } from 'react';
import GlimpsesCard from './glimpsesCard';

const MobileAnim = ({
  Glimpses,
  glimpsesContainerRef,
}: {
  Glimpses: any;
  glimpsesContainerRef: any;
}) => {
  const targetRefs = useRef<(HTMLDivElement | null)[]>([]);
  const parentDiv = useRef<HTMLDivElement | null>(null);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const arrowRef = useRef<HTMLDivElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [arrowPosition, setArrowPosition] = useState({
    top: 0,
    left: 0,
  });
  const [currentTarget, setCurrentTarget] = useState(-1);
  const [isInView, setIsInView] = useState(false);
  const timerRef = useRef<any>(null);
  const heightDiffBetweenItems = 237;
  const debounceTimeout = useRef<any | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        clearTimeout(debounceTimeout.current); // Clear any previous timeout

        if (entry.isIntersecting) {
          debounceTimeout.current = setTimeout(() => {
            setIsInView(true);

            // Scroll the target into view and center it
            glimpsesContainerRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
            setArrowPosition({
              left: 0,
              top: 0,
            });
            setTimeout(() => {
              setCurrentTarget(0);
            }, 1000);
          }, 100); // Adjust this timeout value as needed
        } else {
          setIsInView(false);
          setCurrentTarget(-1);
          clearTimeout(timerRef.current); // Stop any ongoing animation
          setArrowPosition({
            left: 0,
            top: 0,
          });
        }
      },
      { threshold: 0.35 } // Adjust threshold as needed
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      clearTimeout(debounceTimeout.current);
      observer.disconnect();
    };
  }, [sectionRef.current]);

  // Move arrow to the given target index
  const moveArrow = (targetIndex: number) => {
    if (targetRefs.current[targetIndex] && isInView) {
      const top = heightDiffBetweenItems * targetIndex;
      setArrowPosition({
        top,
        left: 0,
      });

      // Scroll the target into view and center it
      targetRefs.current[targetIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center', // Center the target vertically in the viewport
      });
    }
  };

  useEffect(() => {
    if (currentTarget === -1) return; // No need to run if currentTarget is not set

    moveArrow(currentTarget);
    if (currentTarget === 0) {
      timerRef.current = setTimeout(() => {
        setCurrentTarget(currentTarget + 1);
      }, 1000);
    }
    // Move to the next target after 2 seconds
    else if (currentTarget < targetRefs.current.length - 1 || currentTarget === 3) {
      timerRef.current = setTimeout(() => {
        setIsPlaying(false);
        setTimeout(() => {
          setIsPlaying(true);
        }, 500);

        setCurrentTarget(currentTarget + 1);
      }, 2000);
    }

    // Clean up on component unmount or when currentTarget changes
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timerRef.current);
  }, [currentTarget]);

  return (
    <div ref={sectionRef}>
      <div
        className="relative flex flex-col items-center h-[948px] translate-x-[16px] w-[calc(100%-32px)]"
        ref={parentDiv}
      >
        {/* Horizontal dotted line */}
        <div className="relative w-full">
          <div className="absolute inset-x-0 flex justify-between z-[1]">
            {[...Array(4)].map((_, index) => (
              <div
                key={randomId()}
                className="rounded-full translate-x-[-50%]"
                style={{
                  position: 'absolute',
                  top: `${index * heightDiffBetweenItems}px`,
                  left: '0px',
                }}
                ref={(el) => {
                  targetRefs.current[index] = el;
                }}
              >
                {index === currentTarget - 1 ? (
                  <div className="image-containers">
                    <img
                      src="/images/masterclassDescription/target.svg"
                      className="images image1"
                      alt="1"
                    />
                    <img
                      src="/images/masterclassDescription/check.svg"
                      className="images image2"
                      alt="1"
                    />
                  </div>
                ) : currentTarget > index ? (
                  <img
                    src="/images/masterclassDescription/check.svg"
                    alt="check"
                    className={`!min-w-6 !min-h-6 ${
                      index + 1 === currentTarget
                        ? 'transition-opacity duration-2000 ease-out opacity-100'
                        : 'opacity-100'
                    }`}
                  />
                ) : (
                  <img
                    src="/images/masterclassDescription/target.svg"
                    alt="target"
                    className="!min-w-6 !min-h-6 transition-opacity"
                  />
                )}
              </div>
            ))}

            {parentDiv.current && (
              <div
                ref={arrowRef}
                className="arrow bg-white rounded-full"
                style={{
                  top: `${arrowPosition.top}px`,
                  left: `${arrowPosition.left}px`,
                  transition: 'top 2s',
                  transform: 'translate(-50%, 0%) rotate(90deg)',
                }}
              >
                <img
                  src="/images/masterclassDescription/arrow.svg"
                  alt="arrow"
                  className={`${arrowPosition.left === 0 ? 'opacity-80' : 'opacity-100'}`}
                />
              </div>
            )}
          </div>

          <div className="relative">
            {[...Array(4)].map((_, index) => (
              <div
                key={randomId()}
                style={{
                  position: 'absolute',
                  top: `${index * heightDiffBetweenItems}px`,
                  right: '0px',
                }}
                ref={(el) => {
                  targetRefs.current[index] = el;
                }}
              >
                {Glimpses && (
                  <GlimpsesCard
                    glimpsesInfo={Glimpses[index]}
                    cardIndex={index}
                    currentTarget={currentTarget}
                    isMobile
                    isPlaying={isPlaying}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <div
          className="relative"
          style={{
            height: heightDiffBetweenItems * 3, // Changed from width to height
            left: '-50%', // Center it horizontally
          }}
        >
          {[...Array(3)].map((_, index) => (
            <div
              key={randomId()}
              className={`absolute border-l ${
                // Changed border-t to border-l for vertical line
                currentTarget > index + 1 || currentTarget === 4
                  ? 'border-green-500 border-solid'
                  : 'border-red-500 border-dashed'
              }`}
              style={{
                height: '33.33%', // Changed from width to height
                top: `${index * 33.33}%`, // Changed from left to top
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default MobileAnim;
